import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Sparkles, Check, Music2, Mic, Palette, Star, ShieldCheck, ArrowRight, X, Radio, ShoppingBag,
} from "lucide-react";
import { Link } from "react-router-dom";
import MarketingUpgradeCard from "@/components/MarketingUpgradeCard";

const PERKS = [
  "Verified Influencer badge on your profile & every review you leave",
  "Access the full customer directory — same tool businesses use",
  "Post reviews about the customers you interact with",
  "Eligible for the $35/mo Marketing add-on to email opted-in customers",
  "Great for bands, actors, artists, comedians, podcasters, and creators",
  "Cancel any time — badge stays for the paid period",
];

const AUDIENCES = [
  { Icon: Music2, label: "Bands" },
  { Icon: Mic, label: "Comedians" },
  { Icon: Palette, label: "Artists" },
  { Icon: Star, label: "Actors" },
  { Icon: Sparkles, label: "Creators" },
];

export default function InfluencerSubscribe() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login?next=/influencer/subscribe", { replace: true });
      return;
    }
    api.get("/subscription/influencer/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ is_influencer: false }));
  }, [user, navigate]);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/influencer/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post("/subscription/influencer/cancel");
      toast.success("Influencer subscription will end at the current period close.");
      const { data } = await api.get("/subscription/influencer/status");
      setStatus(data);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    } finally {
      setCancelling(false);
    }
  };

  const resume = async () => {
    setResuming(true);
    try {
      await api.post("/subscription/influencer/resume");
      toast.success("Influencer subscription resumed.");
      const { data } = await api.get("/subscription/influencer/status");
      setStatus(data);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume.");
    } finally {
      setResuming(false);
    }
  };

  const isActive = !!status?.is_influencer;
  const willCancel = !!status?.influencer_cancel_at_period_end;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-14" data-testid="influencer-subscribe-page">
      {/* Hero */}
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#B31E1E]">
          <Sparkles size={12} /> Influencer plan
        </div>
        <h1 className="mt-4 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614]">
          Your following deserves a verified badge.
        </h1>
        <p className="mt-4 text-[17px] text-[#5C4E48] max-w-2xl mx-auto">
          Bands, actors, artists, comedians, creators — anyone with an audience.
          $9.95/mo unlocks the same customer tools businesses use, plus a verified Influencer badge across the platform.
        </p>

        {/* Audience chips */}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {AUDIENCES.map(({ Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-[#EAE3DE] bg-white px-3 py-1.5 text-[13px] font-medium text-[#3E2A26]">
              <Icon size={13} className="text-[#B31E1E]" /> {label}
            </span>
          ))}
        </div>
      </div>

      {/* Active-influencer creator toolkit — Go Live, run a shop, view stats */}
      {isActive && (
        <Card className="mt-8 rounded-2xl border border-[#B31E1E]/20 bg-gradient-to-br from-red-50/50 to-white p-6" data-testid="influencer-creator-toolkit">
          <div className="flex items-center gap-2">
            <Radio size={16} className="text-[#B31E1E]" />
            <h3 className="font-display text-xl tracking-tight">Creator toolkit</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600">You're verified — now go make some noise.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/marketplace/live/new">
              <Button className="rounded-[35px] bg-red-600 hover:bg-red-700 text-white" data-testid="influencer-go-live">
                <Radio size={13} className="mr-1.5" /> Go live
              </Button>
            </Link>
            <Link to="/shop/manage">
              <Button variant="outline" className="rounded-[35px]" data-testid="influencer-manage-shop">
                <ShoppingBag size={13} className="mr-1.5" /> My shop
              </Button>
            </Link>
            <Link to="/directory">
              <Button variant="outline" className="rounded-[35px]">
                Customer directory
              </Button>
            </Link>
          </div>
          {status && !status.marketing_active && (
            <div className="mt-5" data-testid="influencer-marketing-upsell">
              <p className="text-xs text-gray-500 mb-2">Unlock every selling tool with the Marketing add-on:</p>
              <MarketingUpgradeCard />
            </div>
          )}
        </Card>
      )}

      {/* Main plan card */}
      <Card
        className="mt-10 rounded-3xl border border-[#B31E1E]/25 bg-white p-8 md:p-10 shadow-[0_20px_60px_-24px_rgba(179,30,30,0.25)]"
        data-testid="influencer-plan-card"
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[#B31E1E]">
              <ShieldCheck size={12} /> Verified Influencer
            </div>
            <h2 className="mt-2 text-3xl font-black text-[#1B1614]">Influencer</h2>
            <p className="mt-1 text-sm text-[#5C4E48] max-w-md">
              Verified badge, customer directory access, and marketing tools — for the price of a couple of coffees.
            </p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-black tracking-tight text-[#1B1614]">
              $9.95<span className="text-base text-[#8B7B73] font-normal">/mo</span>
            </div>
            <div className="text-xs text-[#8B7B73] mt-1">USD · Cancel anytime</div>
          </div>
        </div>

        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 text-[15px] text-[#3E2A26]">
          {PERKS.map((l) => (
            <li key={l} className="flex items-start gap-2">
              <Check size={16} className="text-[#B31E1E] shrink-0 mt-1" /> {l}
            </li>
          ))}
        </ul>

        {/* CTA area — states: not-subscribed / active-renewing / active-cancelling */}
        <div className="mt-10 pt-6 border-t border-[#EAE3DE]">
          {status === null ? (
            <div className="h-11 rounded-lg bg-[#FAF5F2] animate-pulse" data-testid="influencer-status-loading" />
          ) : isActive ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="influencer-active-state">
              <div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider text-emerald-700">
                  <ShieldCheck size={12} /> Active
                </div>
                <p className="mt-2 text-sm text-[#5C4E48]">
                  {willCancel ? (
                    <>Your subscription ends at the current period close. Resume any time before then.</>
                  ) : (
                    <>You&apos;re a verified Influencer. Renews monthly — cancel any time.</>
                  )}
                </p>
              </div>
              {willCancel ? (
                <Button
                  onClick={resume}
                  disabled={resuming}
                  data-testid="influencer-resume-btn"
                  className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white h-11 px-6 font-semibold"
                >
                  {resuming ? "Resuming…" : "Resume subscription"}
                </Button>
              ) : (
                <Button
                  onClick={cancel}
                  disabled={cancelling}
                  variant="outline"
                  data-testid="influencer-cancel-btn"
                  className="rounded-lg border-[#EAE3DE] bg-white hover:bg-[#FAF5F2] h-11 px-6 font-semibold text-[#3E2A26]"
                >
                  <X size={16} className="mr-1.5" /> {cancelling ? "Cancelling…" : "Cancel at period end"}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-[#5C4E48]">Get the verified Influencer badge and unlock customer tools instantly.</p>
              <Button
                onClick={startCheckout}
                disabled={loading}
                data-testid="influencer-subscribe-btn"
                className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white h-11 px-6 font-semibold"
              >
                {loading ? "Starting checkout…" : (
                  <>Subscribe for $9.95/mo <ArrowRight size={16} className="ml-2" /></>
                )}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Footnote */}
      <p className="mt-6 text-center text-xs text-[#8B7B73]">
        Payment is processed securely by Stripe. No long-term contracts. USD only.
      </p>
    </div>
  );
}
